import PropTypes from 'prop-types';
import './style.scss';

import { useMemo, useEffect } from 'react';

import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useTranslation } from 'react-i18next';

import {
  updateFinancialAdjustment,
  getFinancialAdjustmentById,
} from 'apis/financialAdjustment.api';

import { ADJUSTMENT_COMMENTS, TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

import Loading from 'components/Loading';
import { createFinancialAdjustmentSchema } from 'features/Project/validation';
import { defaultFinancialAdjustment } from 'features/Project/defaultValues';
import { getProjectById } from 'apis/project.api';
import { useGetLastAdjustmentComments } from 'utils/hooks/adjustmentComments';
import FinancialAdjustmentForm from '../../components/FinancialAdjustmentForm';

const formOptions = { resolver: yupResolver(createFinancialAdjustmentSchema) };

function EditFinancialAdjustment({ readOnly }) {
  // #region Data
  const { t } = useTranslation();
  const { projectId, financialAdjustmentId } = useParams();

  const { control, handleSubmit, watch, setValue, getValues, reset } = useForm({
    mode: 'onChange',
    ...formOptions,
    shouldFocusError: true,
    defaultValues: defaultFinancialAdjustment,
  });

  const { errors, isDirty, dirtyFields, isValid } = useFormState({
    control,
  });

  const {
    data: projectData,
    isLoading: isProjectDataLoading,
    refetch,
  } = useQuery(['project', projectId], () => getProjectById(projectId));
  const project = useMemo(() => projectData?.data, [projectData]);

  const { data: financialAdjustmentData, isLoading: isFinancialAdjustmentDataLoading } = useQuery(
    ['financialAdjustment', financialAdjustmentId],
    () => getFinancialAdjustmentById(financialAdjustmentId)
  );
  const financialAdjustment = useMemo(
    () => financialAdjustmentData?.data,
    [financialAdjustmentData]
  );

  const { data: lastComment } = useGetLastAdjustmentComments({
    adjustmentName: ADJUSTMENT_COMMENTS.FINANCIAL_ADJUSTMENT,
    adjustmentId: financialAdjustmentId,
  });

  const { mutateAsync: updateMuteAsync, isLoading: isUpdatingLoading } =
    useMutation(updateFinancialAdjustment);
  const onSubmit = async (data) => {
    try {
      await updateMuteAsync({ body: { ...data, projectId }, id: financialAdjustmentId });

      toast(TOAST.SUCCESS, t('financialAdjustment.notification.update'));
      refetch();
      const currrentValues = getValues();
      reset(currrentValues);
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };
  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  useEffect(() => {
    setValue('commitment', financialAdjustment?.commitment);
    setValue('otherExpenditure', financialAdjustment?.otherExpenditure || 0);
    setValue(
      'financialAdjustmentItems',
      financialAdjustment?.financialAdjustmentItems?.map(
        ({ proposedExpenditureType, ...rest }) => ({
          ...rest,
          subProposedExpenditureTypeId: proposedExpenditureType?.subProposedExpenditureTypeId,
        })
      )
    );
  }, [JSON.stringify(financialAdjustment)]);

  if (isProjectDataLoading || isFinancialAdjustmentDataLoading) {
    return <Loading />;
  }

  return (
    <div className="financial-adjustment-container">
      <FinancialAdjustmentForm
        control={control}
        errors={errors}
        watch={watch}
        reset={reset}
        refetch={refetch}
        setValue={setValue}
        getValues={getValues}
        handleSubmit={handleSubmit(onSubmit)}
        checkValidation={checkValidation}
        checkKeyDown={checkKeyDown}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        footerConfirmationPrompt={footerConfirmationPrompt}
        project={project}
        readOnly={!!financialAdjustment?.submittedAt || readOnly}
        financialAdjustmentId={financialAdjustmentId}
        hasContractFile={!project?.fileUrl}
        lastComment={lastComment}
      />
      {isUpdatingLoading && <Loading />}
    </div>
  );
}

EditFinancialAdjustment.propTypes = {
  readOnly: PropTypes.bool,
};

EditFinancialAdjustment.defaultProps = { readOnly: false };

export default EditFinancialAdjustment;
